import { format } from "date-fns";
import { NoteManyQuery } from "gql/generated";
import { useMemo, useState } from "react";
import { useLoggedInAs } from "shared/hooks";
import { useAppContext } from "useAppContext";
import { useNotesContext } from "shared/contextProviders";
import { DATE_PATTERN_SIMPLE, DATE_PATTERN_STANDARD } from "shared/utils";
import { toZonedTime } from "date-fns-tz";
import { NoteFormValues } from "./useNoteCardForm";
import { UseFormReturn, useWatch } from "react-hook-form";

export type UseNoteCardProps = {
  initialMode: "view" | "edit";
  note?: NoteManyQuery["noteMany"][number];
  form: UseFormReturn<NoteFormValues>;
  defaultValues: NoteFormValues | undefined;
  onCancel?: () => void;
  onArchiveSuccess?: () => void;
  onDeleteSuccess?: () => void;
  /** If provided, confirm delete calls this instead of deleteNote (parent runs exit animation then deletes). */
  onDeleteRequested?: (note: NoteManyQuery["noteMany"][number]) => void;
};

export const useNoteCard = ({
  initialMode,
  note,
  form,
  defaultValues,
  onCancel,
  onArchiveSuccess,
  onDeleteSuccess,
  onDeleteRequested,
}: UseNoteCardProps) => {
  const { dbUser } = useAppContext();
  const loggedInAs = useLoggedInAs();

  const [mode, setMode] = useState<"view" | "edit">(initialMode);

  const message = useWatch({ control: form.control, name: "message" });
  const noteDateISO = useWatch({ control: form.control, name: "noteDateISO" });
  const entityId = useWatch({ control: form.control, name: "entity.id" });

  const maxDate = useMemo(() => format(new Date(), DATE_PATTERN_SIMPLE), []);
  const displayDate = useMemo(
    () =>
      note
        ? note.noteDateISO
          ? format(toZonedTime(note.noteDateISO, "UTC"), DATE_PATTERN_STANDARD)
          : format(note.createdDateISO ?? "", DATE_PATTERN_STANDARD)
        : "",
    [note],
  );

  const {
    allowArchiving,
    allowDeleting,
    hideDisplayDate,
    archiveNote,
    deleteNote,
    isSavingNote,
    mentionableUsers,
    viewOnly,
  } = useNotesContext();

  /**
   * Edit the note
   */
  const handleEditClick = () => {
    setMode("edit");
  };

  /**
   * Cancel the edit
   */
  const handleCancelClick = () => {
    form.reset(defaultValues);
    onCancel?.();
    // This is so that adding a new note with the "Create timeline note" button
    // starts in the edit mode
    if (note) {
      setMode("view");
    }
  };

  /**
   * Archive the note
   * @returns void
   */
  const handleArchiveClick = async () => {
    if (!dbUser || !loggedInAs || !note) {
      return;
    }

    const success = await archiveNote(note._id, true);
    if (success) {
      setMode("view");
      onArchiveSuccess?.();
    }
  };

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  /**
   * Delete the note (or request deferred delete for parent to animate first)
   */
  const handleDeleteConfirmClick = async () => {
    if (!dbUser || !loggedInAs || !note) {
      return;
    }
    setIsDeleteConfirmOpen(false);
    if (onDeleteRequested) {
      onDeleteRequested(note);
      return;
    }
    setIsDeleting(true);
    const success = await deleteNote(note._id);
    setIsDeleting(false);
    if (success) {
      setMode("view");
      onDeleteSuccess?.();
    }
  };

  return {
    canEdit: note?.owner?.entityId === loggedInAs?.entityId && !viewOnly,
    mode,
    setMode,
    allowArchiving,
    allowDeleting,
    createdBy: note ? `${note.author?.firstName} ${note.author?.lastName}` : "",
    displayDate,
    hideDisplayDate,
    isSaving: isSavingNote,
    mentionableUsers,
    message,
    noteDateISO,
    entityId,
    maxDate,
    handleEditClick,
    handleCancelClick,
    handleArchiveClick,
    isDeleteConfirmOpen,
    setIsDeleteConfirmOpen,
    handleDeleteConfirmClick,
    isDeleting,
  };
};
